import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  Alert,
  Chip,
  Tabs,
  Tab
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LogoutIcon from '@mui/icons-material/Logout';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import FolderIcon from '@mui/icons-material/Folder';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CampaignIcon from '@mui/icons-material/Campaign';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { apiService, Client, Bundle } from '../services/api';
import CreateClientModal from './CreateClientModal';
import EditClientModal from './EditClientModal';
import CreateBundleModal from './CreateBundleModal';
import CreateAdModal from './CreateAdModal';

interface AdminDashboardProps {
  adminSecret: string;
  onLogout: () => void;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ adminSecret, onLogout }) => {
  const [tabValue, setTabValue] = useState(0);
  const [clients, setClients] = useState<Client[]>([]);
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [createBundleModalOpen, setCreateBundleModalOpen] = useState(false);
  const [createAdModalOpen, setCreateAdModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedBundle, setSelectedBundle] = useState<Bundle | null>(null);
  const [clientForAd, setClientForAd] = useState<Client | null>(null);

  const fetchClients = async () => {
    setIsLoading(true);
    setError('');
    try {
      const clientList = await apiService.getAllClients(adminSecret);
      setClients(clientList);
    } catch (error: any) {
      setError('Failed to fetch clients');
      console.error('Error fetching clients:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBundles = async () => {
    setIsLoading(true);
    setError('');
    try {
      const bundleList = await apiService.getAllBundles(adminSecret);
      setBundles(bundleList);
    } catch (error: any) {
      setError('Failed to fetch bundles');
      console.error('Error fetching bundles:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (tabValue === 0) {
      fetchClients();
    } else if (tabValue === 1) {
      fetchBundles();
    }
  }, [adminSecret, tabValue]);

  const handleClientClick = (client: Client) => {
    setSelectedClient(client);
    setEditModalOpen(true);
  };

  const handleClientCreated = () => {
    fetchClients();
  };

  const handleClientUpdated = () => {
    fetchClients();
  };

  const handleBundleCreated = () => {
    fetchBundles();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEditBundle = (bundle: Bundle) => {
    setSelectedBundle(bundle);
    setCreateBundleModalOpen(true);
  };

  const handleDeleteBundle = async (bundleId: number) => {
    if (window.confirm('Are you sure you want to delete this bundle? This action cannot be undone.')) {
      try {
        await apiService.deleteBundle(adminSecret, bundleId);
        fetchBundles(); // Refresh the list
      } catch (error) {
        console.error('Error deleting bundle:', error);
        setError('Failed to delete bundle');
      }
    }
  };

  const handleBundleModalClose = () => {
    setCreateBundleModalOpen(false);
    setSelectedBundle(null);
  };

  const handleCreateAd = (client: Client, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent the client click event
    setClientForAd(client);
    setCreateAdModalOpen(true);
  };

  const handleViewClientAds = (client: Client, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent the client click event
    // Open login page with client secret in new tab, which will auto-redirect to ads
    const clientSecret = client.tx_client_secret;
    const url = `/login?client_secret=${clientSecret}`;
    window.open(url, '_blank');
  };

  const handleAdCreated = () => {
    // You might want to refresh some data here
    console.log('Ad created successfully');
  };

  const handleCreateAdModalClose = () => {
    setCreateAdModalOpen(false);
    setClientForAd(null);
  };

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <AdminPanelSettingsIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Admin Dashboard
          </Typography>
          <IconButton color="inherit" onClick={onLogout}>
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="admin tabs">
              <Tab label="Client Management" />
              <Tab label="Bundle Management" />
            </Tabs>
          </Box>

          {tabValue === 0 && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1">
                  Client Management
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateModalOpen(true)}
                >
                  Create New Client
                </Button>
              </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {isLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography>Loading clients...</Typography>
            </Box>
          ) : clients.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                No clients found
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                Create your first client to get started
              </Typography>
            </Box>
              ) : (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Clients ({clients.length})
                  </Typography>
                  <List>
                    {clients.map((client, index) => (
                      <React.Fragment key={client.id}>
                        <ListItem
                          secondaryAction={
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={(e) => handleViewClientAds(client, e)}
                              >
                                View Ads
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<CampaignIcon />}
                                onClick={(e) => handleCreateAd(client, e)}
                              >
                                Create Ad
                              </Button>
                            </Box>
                          }
                        >
                          <ListItemButton onClick={() => handleClientClick(client)} sx={{ pr: 20 }}>
                            <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="h6">
                                    {client.tx_name}
                                  </Typography>
                                  <Chip 
                                    label={`ID: ${client.id}`} 
                                    size="small" 
                                    variant="outlined" 
                                  />
                                  {client.bundles && client.bundles.length > 0 && (
                                    <Chip 
                                      label={`${client.bundles.length} bundle(s)`} 
                                      size="small" 
                                      color="primary"
                                      variant="outlined" 
                                    />
                                  )}
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Typography variant="body2" color="text.secondary">
                                    Click to view and manage client details
                                  </Typography>
                                  {client.bundles && client.bundles.length > 0 && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                      {client.bundles.map((bundle, bundleIndex) => (
                                        <Chip
                                          key={bundleIndex}
                                          label={`${bundle.tx_name} v${bundle.tx_version}`}
                                          size="small"
                                          variant="outlined"
                                          color="secondary"
                                        />
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                        {index < clients.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </Box>
              )}
            </>
          )}

          {tabValue === 1 && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1">
                  Bundle Management
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateBundleModalOpen(true)}
                >
                  Create New Bundle
                </Button>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {isLoading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography>Loading bundles...</Typography>
                </Box>
              ) : bundles.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    No bundles found
                  </Typography>
                  <Typography color="text.secondary" sx={{ mt: 1 }}>
                    Create your first bundle to get started
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Bundles ({bundles.length})
                  </Typography>
                  <List>
                    {bundles.map((bundle, index) => (
                      <React.Fragment key={bundle.id}>
                        <ListItem
                          secondaryAction={
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton
                                edge="end"
                                aria-label="edit"
                                onClick={() => handleEditBundle(bundle)}
                                color="primary"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                edge="end"
                                aria-label="delete"
                                onClick={() => handleDeleteBundle(bundle.id)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          }
                        >
                          <FolderIcon sx={{ mr: 2, color: 'primary.main' }} />
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="h6">
                                  {bundle.tx_name}
                                </Typography>
                                <Chip 
                                  label={`Version: ${bundle.tx_version}`} 
                                  size="small" 
                                  variant="outlined" 
                                />
                              </Box>
                            }
                            secondary={
                              <Typography variant="body2" color="text.secondary">
                                Bundle ID: {bundle.id} • {bundle.bundle_ads?.length || 0} ads
                              </Typography>
                            }
                          />
                        </ListItem>
                        {index < bundles.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Container>

      <CreateClientModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        adminSecret={adminSecret}
        onClientCreated={handleClientCreated}
      />

      <EditClientModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        client={selectedClient}
        adminSecret={adminSecret}
        onClientUpdated={handleClientUpdated}
      />

      <CreateBundleModal
        open={createBundleModalOpen}
        onClose={handleBundleModalClose}
        adminSecret={adminSecret}
        onBundleCreated={handleBundleCreated}
        editBundle={selectedBundle}
      />

      <CreateAdModal
        open={createAdModalOpen}
        onClose={handleCreateAdModalClose}
        client={clientForAd}
        adminSecret={adminSecret}
        onAdCreated={handleAdCreated}
      />
    </>
  );
};

export default AdminDashboard;